import { useEffect, useLayoutEffect, useState } from 'preact/hooks';
import type { CampaignPromo, CheckoutCallbacks, FunnelSurface, OnsitePrice, ProductConfig, ReturnedTrialSetup } from '@/types';
import { checkProductAccess, checkItemAccess, getUpgradeOptions, type UpgradeSource } from '@/api/client';
import { hasSession } from '@/session';
import { isItemScoped, resolveItemForPrice } from '@/page';
import { logError, logWarn } from '@/sdkConsole';
import { CheckoutFlow } from '@/components/CheckoutFlow';
import { useCheckoutPresentation } from '@/components/CheckoutPresentation';
import { CheckoutShell } from '@/components/CheckoutShell';
import { ErrorStep } from '@/components/ErrorStep';
import { runHook } from '@/components/runHook';
import { t, useT } from '@/i18n/t';
import { SupportEmailProvider } from '@/supportContact';

interface Props extends CheckoutCallbacks {
  config: ProductConfig;
  onHidden?: () => void;
  // The product key the paywall was opened for (showPaywall({ product })).
  productKey: string;
  // The item supplied to showPaywall({ product, item }), opting a runtime
  // item-scoped price into the paywall. Page-sourced item prices ignore it.
  item?: string;
  // When true the visitor can close the paywall; otherwise it stays blocking.
  dismissible?: boolean;
  // Set when resuming with a previously chosen price (BYO login resume, or the
  // secure-context redirect resume): the externalId of that price, so the flow
  // advances straight past the plan picker.
  initialPriceExternalId?: string;
  // Set when resuming a secure-context redirect for a variable donation: the
  // amount (cents) the visitor had chosen.
  initialAmountCents?: number;
  // Set when the resumed selection was a trial start.
  initialTrial?: boolean;
  // Set when the page is Stripe's return from a trial card check's 3DS
  // redirect: the confirmed SetupIntent (with its promo) the flow resumes the
  // trial on instead of starting a fresh card check.
  trialSetupResume?: ReturnedTrialSetup;
  onTrialSetupResumeConsumed?: () => void;
  // Prefills the guest email field (a publisher-supplied showPaywall { email }).
  initialEmail?: string;
  // The mount's campaign code (URL ?promo= stash or the showPaywall promoCode
  // option): the picker shows its discounted prices and the payment step
  // auto-applies it.
  campaignPromo?: CampaignPromo | null;
  // Funnel surface override, threaded to CheckoutFlow (openCheckout passes
  // 'checkout'). Absent leaves CheckoutFlow's product-type derivation intact.
  surface?: FunnelSurface;
  // True when re-opening a previously counted selection (BYO login return,
  // http→https resume); threaded to CheckoutFlow, which skips re-emitting
  // price_selected. A fresh openCheckout mount leaves it unset.
  resumed?: boolean;
}

// Paywall renders a product's prices. The publisher decides whether to mount it
// (via ezsubscriptions.showPaywall); this component still verifies access
// server-side and self-suppresses if the visitor already holds the product (or
// the in-play item, for item-scoped prices).
export function Paywall({ config, productKey, item, dismissible, initialPriceExternalId, initialAmountCents, initialTrial, trialSetupResume, onTrialSetupResumeConsumed, initialEmail, campaignPromo, surface, resumed, onSuccess, onCancel, onError, onHidden }: Props) {
  useT();
  const { inline, successUrl, isActive } = useCheckoutPresentation();
  const [accessError, setAccessError] = useState<string | null>(null);
  const paywallProducts = config.product ? [config.product] : [];
  const isGated = paywallProducts.length > 0;
  const [upgrade, setUpgrade] = useState<UpgradeSource | undefined>();
  const [upgradeMaskedEmail, setUpgradeMaskedEmail] = useState<string | undefined>();
  const [hasAccess, setHasAccess] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Anonymous visitors never call the backend, so only block on the access check
  // when there is a session to check.
  const [checkingAccess, setCheckingAccess] = useState(isGated && hasSession());

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (!isGated) {
        return;
      }

      // Anonymous visitors (no session) never call the backend: show paywall.
      if (!hasSession()) {
        setUnlocked(false);
        return;
      }

      try {
        // A preselected price (openCheckout) scopes the suppression to that
        // price's grant; a bare paywall checks the product then any item.
        const preselectedPrice = initialPriceExternalId
          ? config.product?.prices.find((p) => p.externalId === initialPriceExternalId)
          : undefined;
        const [accessReason, options] = await Promise.all([
          resolveAccess(productKey, config.product?.prices ?? [], item, surface === 'checkout' ? 'openCheckout' : 'showPaywall', preselectedPrice),
          getUpgradeOptions(productKey, initialPriceExternalId).catch((err) => {
            logError('upgrade options unavailable; using existing checkout behavior', err);
            return null;
          }),
        ]);
        if (cancelled || (isActive && !isActive())) {
          return;
        }
        const allowed = accessReason !== null;
        const source = options?.sources[0];
        const canUpgrade = source && (source.inProgress || (!source.blockedReason && source.targets?.length > 0) || source.blockedReason);
        if (accessReason && !canUpgrade) {
          logWarn(accessReason);
        }
        setUpgrade(canUpgrade ? source : undefined);
        setUpgradeMaskedEmail(options?.maskedEmail);
        setHasAccess(allowed);
        setUnlocked(allowed && !canUpgrade);
        setCheckingAccess(false);
      } catch (err) {
        if (cancelled || (isActive && !isActive())) {
          return;
        }
        // The access check errored (network/backend). Render the gate either
        // way — for a paywall guarding content that's fail-closed (don't leak
        // content); for a direct openCheckout it's fail-open (a purchase the
        // visitor explicitly asked for still mounts). Same render outcome, so
        // no flag is needed; surface the failure so an erroring endpoint isn't
        // silent.
        if (inline) {
          const message = t('inline.accessFailed');
          setAccessError(message);
          runHook(onError, 'onError', { message });
        }
        logWarn('could not verify access; showing the gate', err);
        setUnlocked(false);
        setCheckingAccess(false);
      }
    }

    check();

    return () => {
      cancelled = true;
    };
  }, [isGated, productKey, item]);

  useLayoutEffect(() => {
    if (!isGated || unlocked || dismissed) {
      onHidden?.();
    }
  }, [isGated, unlocked, dismissed]);

  const handleUnlock = () => {
    setUnlocked(true);
  };

  // Closing a dismissible paywall unmounts the gate; the publisher's onCancel is
  // fired by CheckoutFlow (only when no checkout succeeded), so this handler is
  // purely the chrome behavior. The page scroll lock is released by CheckoutFlow
  // unmounting (see pageScrollLock).
  const handleDismiss = () => {
    setDismissed(true);
  };

  if (isActive && !isActive()) {
    return null;
  }
  if (inline && accessError) {
    return <ErrorStep message={accessError} />;
  }
  if (inline && (unlocked || checkingAccess)) {
    const label = checkingAccess ? t('inline.checkingAccess') : t('success.title.owned');
    return <CheckoutShell ariaLabel={label} currentStep={3} hideSteps>
      <h2 class="sm-title" role="status">{label}</h2>
      {unlocked && successUrl && <a class="sm-btn sm-btn-primary" href={successUrl}>{t('cta.continue')}</a>}
    </CheckoutShell>;
  }
  if (!isGated || unlocked || dismissed || checkingAccess) {
    return null;
  }

  return (
    <SupportEmailProvider value={config.supportEmail}>
      <CheckoutFlow
        upgrade={upgrade}
        upgradeMaskedEmail={upgradeMaskedEmail}
        domainId={config.domainId}
        authMode={config.authMode}
        guestCheckoutEnabled={config.guestCheckoutEnabled}
        expeditedCheckoutEnabled={config.expeditedCheckoutEnabled}
        walletsEnabled={config.walletsEnabled}
        initialEmail={initialEmail}
        checkoutText={config.checkoutText}
        products={paywallProducts}
        productKey={productKey}
        campaignPromo={campaignPromo}
        promoPreview={config.promo}
        item={item}
        initialPriceExternalId={initialPriceExternalId}
        initialAmountCents={initialAmountCents}
        initialTrial={initialTrial}
        trialSetupResume={trialSetupResume}
        onTrialSetupResumeConsumed={onTrialSetupResumeConsumed}
        surface={surface}
        resumed={resumed}
        onUnlock={handleUnlock}
        // A holder's upgrade offer, or a blocker-only screen, is always closable.
        onDismiss={dismissible || (upgrade && (hasAccess || !!upgrade.blockedReason)) ? handleDismiss : undefined}
        // Always wired, dismissible or not: a BYO publisher's login modal opens
        // in-page, and even a blocking gate must unmount to uncover it.
        onExternalAuthExit={inline ? undefined : handleDismiss}
        onSuccess={onSuccess}
        onCancel={onCancel}
        onError={onError}
      />
    </SupportEmailProvider>
  );
}

// resolveAccess decides whether the visitor already has access, so a redundant
// showPaywall self-suppresses. The visitor has access if they hold the product,
// or — for any item-scoped price in play (a page-sourced price, or a runtime
// price the paywall was opened with an item for) — if they already bought that
// item. Return the suppression reason; the caller logs it only if no upgrade opens.
async function resolveAccess(productKey: string, prices: OnsitePrice[], suppliedItem?: string, api: string = 'showPaywall', preselectedPrice?: OnsitePrice): Promise<string | null> {
  // A preselected price (openCheckout) is scoped to what THAT price grants: an
  // item-scoped price checks only the item — a subscriber to the parent product
  // must still be able to buy a single item — and a product-scoped price checks
  // the product. A paywall (no preselected price) checks the product, then any
  // in-play item.
  if (preselectedPrice) {
    if (isItemScoped(preselectedPrice)) {
      const item = resolveItemForPrice(preselectedPrice, suppliedItem);
      if (!item) {
        return null;
      }
      const access = await checkItemAccess(item);
      if (access.decision === 'allowed') {
        return `${api}: not shown — visitor already purchased item "${item}"`;
      }
      return null;
    }
    const product = await checkProductAccess(productKey);
    if (product.decision === 'allowed') {
      return `${api}: not shown — visitor already has access to product "${productKey}"`;
    }
    return null;
  }
  const product = await checkProductAccess(productKey);
  if (product.decision === 'allowed') {
    return `${api}: not shown — visitor already has access to product "${productKey}"`;
  }
  for (const price of prices) {
    if (!isItemScoped(price)) {
      continue;
    }
    const item = resolveItemForPrice(price, suppliedItem);
    if (!item) {
      continue;
    }
    const access = await checkItemAccess(item);
    if (access.decision === 'allowed') {
      return `${api}: not shown — visitor already purchased item "${item}"`;
    }
  }
  return null;
}
